import { zodResolver } from '@hookform/resolvers/zod'
import { router, useLocalSearchParams, useNavigation } from 'expo-router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { View, ViewStyle } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldSelect from '@/components/form/FormFieldSelect'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldVolunteer from '@/components/fosters/cards/formFields/FormFieldVolunteer'
import FormFieldWeight from '@/components/fosters/cards/formFields/FormFieldWeight'
import FosterMedicalForm from '@/components/fosters/cards/FosterMedicalForm'
import fosterSharedStyles from '@/components/fosters/cards/fosterSharedStyles'
import FosterMedicationForm from '@/components/fosters/cards/medications/FosterMedicationForm'
import FosterThumbnail from '@/components/fosters/FosterThumbnail'
import Card from '@/components/shared/Card'
import Page from '@/components/shared/Page'
import Snackbar from '@/components/snackbar/Snackbar'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import Species from '@/enums/species'
import WeightUnit from '@/enums/weightUnit'
import FosterService from '@/services/fosterService'
import { useAppStore } from '@/store/useAppStore'
import { addAction, RumActionType } from '@/utils/datadog/datadog'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

const fosterBulkFormSchema = z.object({
  birthDate: z.date().optional(),
  breed: z.string().optional(),
  clearFecalTestDate: z.date().optional(),
  comboTestingCompletedDate: z.date().optional(),
  currentFood: z.string().optional(),
  fecalFollowUpDate: z.date().optional(),
  felvPositive: z.boolean().optional(),
  fivPositive: z.boolean().optional(),
  fixedBeforeIntake: z.boolean().optional(),
  fixedOnDate: z.date().optional(),
  gender: z.enum(['Male', 'Female']).optional(),
  heartwormPositive: z.boolean().optional(),
  heartwormTestingCompletedDate: z.date().optional(),
  history: z.string().optional(),
  intakeDate: z.date().optional(),
  isAdopted: z.boolean().optional(),
  nextComboMedDue: z.date().optional(),
  nextFleaTickMedDue: z.date().optional(),
  nextHeartwormPreventionDue: z.date().optional(),
  notes: z.string().optional(),
  positiveFecalTestDate: z.date().optional(),
  readyForAdoption: z.boolean().optional(),
  species: z.nativeEnum(Species).optional(),
  userId: z.string().optional(),
  weight: z.string().optional(),
  weightUnit: z.nativeEnum(WeightUnit).optional(),

  medication: z
    .object({
      name: z.string().optional(),
      dueDate: z.date().optional(),
      givenDate: z.date().optional(),
      givenBy: z.string().optional(),
      notes: z.string().optional(),
      isRepeating: z.boolean().optional(),
      frequency: z.number().optional(),
    })
    .optional(),
})

export type FosterBulkForm = z.infer<typeof fosterBulkFormSchema>

const SaveButton = ({
  isLoading,
  disabled,
  onSubmit,
  style,
}: {
  isLoading: boolean
  disabled: boolean
  onSubmit: () => void
  style?: ViewStyle
}) => (
  <UniButton
    disabled={disabled}
    loading={isLoading}
    mode="contained"
    onPress={() => {
      onSubmit()
    }}
    style={style}
  >
    Save
  </UniButton>
)

const FosterBulkEdit = () => {
  const navigation = useNavigation()

  const bulkUpdateMutation = trpc.foster.bulkUpdate.useMutation({
    onSuccess: () => {
      Snackbar.info('Changes saved')
    },
  })
  const currentOrganization = useAppStore.use.currentOrganization().organization

  const { ids } = useLocalSearchParams()
  const idArray = (ids as string).split(',')

  const { fosterList } = FosterService.useFosterList()
  const speciesSet = new Set(
    idArray.map((id) => fosterList.find((f) => f.id === id)?.species)
  )
  const species =
    speciesSet.size === 1
      ? (speciesSet.values().next().value as Species)
      : undefined

  const { control, formState, handleSubmit, setValue } =
    useForm<FosterBulkForm>({
      mode: 'onBlur',
      resolver: zodResolver(fosterBulkFormSchema),
      defaultValues: {
        species: species,
      },
    })

  const submit = handleSubmit((data) => {
    bulkUpdateMutation.mutate({
      ids: idArray,
      ...data,
      weight: data.weight ? parseFloat(data.weight) : undefined,
      weightUnit: data.weightUnit || WeightUnit.Pounds,
      medication: data.medication?.name
        ? { ...data.medication, name: data.medication.name }
        : undefined,
    })
  })

  useEffect(() => {
    navigation.setOptions({
      title: `Edit All`,
      headerRight: () =>
        SaveButton({
          isLoading: bulkUpdateMutation.isPending,
          disabled: !formState.isDirty || bulkUpdateMutation.isPending,
          onSubmit: () => submit(),
        }),
    })
  }, [
    idArray.length,
    navigation,
    submit,
    bulkUpdateMutation.isPending,
    formState.isDirty,
  ])

  return (
    <Page viewId="foster-detail-scroll-view">
      <View style={styles.container}>
        <Card
          title={`Editing ${idArray.length} foster${idArray.length > 1 ? 's' : ''}`}
        >
          <UniText>
            Changes will be applied to each of the following fosters:
          </UniText>
          <View style={styles.fosterThumbnailContainer}>
            {idArray.map((id) => {
              const foster = fosterList.find((f) => f.id === id)
              if (!foster) return null

              return (
                <UniTouchableRipple
                  accessibilityHint={`Opens the foster profile for ${foster.name}.`}
                  accessibilityLabel={foster.name}
                  accessibilityRole="button"
                  borderless
                  key={id}
                  onPress={() => {
                    addAction({
                      type: RumActionType.TAP,
                      name: `View - Foster`,
                    })
                    router.navigate({
                      pathname: buildOrgPath('/fosters/[id]'),
                      params: { id, from: 'fosters' },
                    })
                  }}
                  style={styles.fosterButton}
                >
                  <View>
                    <View style={styles.fosterThumbnail}>
                      <FosterThumbnail
                        blurhash={foster?.blurhash}
                        species={foster?.species as Species}
                        style={[
                          StyleSheet.absoluteFill,
                          styles.fosterThumbnail,
                        ]}
                        thumbUri={foster?.thumbUri}
                      />
                    </View>
                    <UniText
                      style={styles.fosterThumbnailName}
                      variant="titleMedium"
                    >
                      {foster?.name}
                    </UniText>
                  </View>
                </UniTouchableRipple>
              )
            })}
          </View>
        </Card>
      </View>
      <View style={styles.container}>
        <Card title="Foster Details">
          <View style={fosterSharedStyles.textInputContainer}>
            <FormFieldSelect
              control={control}
              data={[
                { label: 'Dog', value: Species.Dog },
                { label: 'Cat', value: Species.Cat },
              ]}
              disable={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="species"
              label="Species"
              required={true}
              style={[styles.fosterDetailInput]}
            />
            <FormFieldVolunteer
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              formState={formState}
              isLoading={false}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
            <FormFieldText
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="breed"
              label="Breed"
              required={true}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
            <FormFieldDate
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="birthDate"
              label="Birth Date"
              required={true}
              style={[styles.fosterDetailInput]}
            />
            <FormFieldDate
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="intakeDate"
              label="Intake Date"
              required={true}
              style={[styles.fosterDetailInput]}
            />
            <FormFieldText
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="currentFood"
              label="Current Food"
              required={true}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
            <FormFieldWeight
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="weight"
              style={[styles.fosterDetailInput]}
            />
            <FormFieldText
              contentStyle={styles.notes}
              control={control}
              disabled={!currentOrganization?.subscriptionActive}
              errors={formState.errors}
              fieldName="notes"
              helperText="Notes entered here will be added to each foster's notes."
              label="Notes"
              multiline
              required={false}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
          </View>
        </Card>
      </View>
      <View style={styles.container}>
        <FosterMedicalForm
          control={control}
          disabled={!currentOrganization?.subscriptionActive}
          formState={formState}
          isLoading={false}
          setValue={setValue}
          showHeartwormTestFields={true}
          species={species}
          submit={() => {}}
        />
      </View>
      <View style={styles.container}>
        <Card title="Add New Medication">
          <FosterMedicationForm
            control={control}
            disabled={!currentOrganization?.subscriptionActive}
            formState={formState}
            pathPrefix="medication"
            setValue={setValue}
            species={species}
          />
        </Card>
      </View>
      <View style={styles.container}>
        <Card title="Adoption Info">
          <FormFieldCheckbox
            control={control}
            disabled={!currentOrganization?.subscriptionActive}
            fieldName="readyForAdoption"
            label="Ready for Adoption"
            required={false}
            status="checked"
          />
          <FormFieldCheckbox
            control={control}
            disabled={!currentOrganization?.subscriptionActive}
            fieldName="isAdopted"
            label="Adopted"
            required={false}
            status="checked"
          />
        </Card>
      </View>
      <SaveButton
        disabled={!formState.isDirty || bulkUpdateMutation.isPending}
        isLoading={bulkUpdateMutation.isPending}
        onSubmit={() => submit()}
        style={styles.bottomSaveButton}
      />
    </Page>
  )
}

export default FosterBulkEdit

const styles = StyleSheet.create((theme) => {
  return {
    bottomSaveButton: {
      marginTop: theme.tokens.spacing[2],
    },
    container: {
      columnGap: theme.tokens.spacing[4],
      flexDirection: 'column',
      rowGap: theme.tokens.spacing[4],
    },
    fosterButton: {
      borderRadius: theme.tokens.containerBorderRadius,
      padding: theme.tokens.spacing[1],
    },
    fosterDetailInput: {
      flex: 1,
      flexBasis: '45%',
      flexGrow: 1,
    },
    fosterThumbnail: {
      borderRadius: theme.tokens.containerBorderRadius,
      height: 80,
      width: 80,
    },
    fosterThumbnailContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.tokens.spacing[4],
      justifyContent: 'flex-start',
      marginTop: theme.tokens.spacing[4],
    },
    fosterThumbnailName: {
      color: theme.colors.primary,
      paddingTop: theme.tokens.spacing[2],
      textAlign: 'center',
    },
    fullWidthFlexBasis: {
      flexBasis: {
        xs: '100%',
        md: '30%',
      },
    },
    notes: {
      minHeight: 150,
    },
  }
})
